import React, { useState, useEffect } from 'react';
import {
  TextField,
  Alert,
  Box,
  Chip,
  Typography,
  FormHelperText
} from '@mui/material';
import { Warning, CheckCircle } from '@mui/icons-material';
import { validateTextForProfanity } from '../utils/profanityFilter';

const ProfanityAwareTextField = ({
  value,
  onChange,
  onValidationChange,
  label,
  placeholder,
  multiline = false,
  rows = 1,
  maxRows,
  required = false,
  disabled = false,
  variant = 'outlined',
  fullWidth = true,
  autoFocus = false,
  showProfanityWarning = true,
  showCleanSuggestion = true,
  realTimeValidation = true,
  ...otherProps
}) => {
  const [validation, setValidation] = useState({
    isValid: true,
    hasProfanity: false,
    profaneWords: [],
    cleanedText: '',
    message: ''
  });
  const [showWarning, setShowWarning] = useState(false);

  // Validate text whenever value changes
  useEffect(() => {
    if (realTimeValidation && value) {
      const validationResult = validateTextForProfanity(value);
      setValidation(validationResult);
      setShowWarning(validationResult.hasProfanity);
      
      // Notify parent component of validation changes
      if (onValidationChange) {
        onValidationChange(validationResult);
      }
    } else {
      setValidation({
        isValid: true,
        hasProfanity: false,
        profaneWords: [],
        cleanedText: value || '',
        message: ''
      });
      setShowWarning(false);
    }
  }, [value, realTimeValidation, onValidationChange]);

  const handleChange = (event) => {
    const newValue = event.target.value;
    
    if (onChange) {
      onChange(event);
    }
  };

  const handleAcceptCleanedText = () => {
    if (onChange && validation.cleanedText) {
      // Create a synthetic event to maintain consistency
      const syntheticEvent = {
        target: {
          value: validation.cleanedText,
          name: otherProps.name || ''
        }
      };
      onChange(syntheticEvent);
      setShowWarning(false);
    }
  };

  const getFieldColor = () => {
    if (!realTimeValidation || !value) return 'primary';
    return validation.hasProfanity ? 'error' : 'success';
  };

  const getHelperText = () => {
    if (!realTimeValidation || !value) return otherProps.helperText || '';
    
    if (validation.hasProfanity) {
      return `Inappropriate language detected: ${validation.profaneWords.join(', ')}`;
    }
    
    return otherProps.helperText || 'Text is appropriate';
  };

  return (
    <Box>
      <TextField
        {...otherProps}
        value={value}
        onChange={handleChange}
        label={label}
        placeholder={placeholder}
        multiline={multiline}
        rows={rows}
        maxRows={maxRows}
        required={required}
        disabled={disabled}
        variant={variant}
        fullWidth={fullWidth}
        autoFocus={autoFocus}
        color={getFieldColor()}
        error={realTimeValidation && validation.hasProfanity}
        helperText={getHelperText()}
        InputProps={{
          ...otherProps.InputProps,
          endAdornment: realTimeValidation && value ? (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
              {validation.hasProfanity ? (
                <Warning color="error" fontSize="small" />
              ) : (
                <CheckCircle color="success" fontSize="small" />
              )}
            </Box>
          ) : otherProps.InputProps?.endAdornment
        }}
      />

      {/* Profanity Warning Alert */}
      {showProfanityWarning && showWarning && validation.hasProfanity && (
        <Alert 
          severity="warning" 
          sx={{ mt: 1 }}
          action={
            showCleanSuggestion && validation.cleanedText !== value ? (
              <Chip
                label="Use Cleaned Version"
                size="small"
                color="primary"
                variant="outlined"
                onClick={handleAcceptCleanedText}
                sx={{ cursor: 'pointer' }}
              />
            ) : null
          }
        >
          <Typography variant="body2">
            <strong>Inappropriate language detected.</strong> Please revise your text to maintain a respectful environment.
          </Typography>
          
          {validation.profaneWords.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Flagged words: 
              </Typography>
              {validation.profaneWords.map((word, index) => (
                <Chip
                  key={index}
                  label={word}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ ml: 0.5, mb: 0.5 }}
                />
              ))}
            </Box>
          )}
          
          {showCleanSuggestion && validation.cleanedText && validation.cleanedText !== value && (
            <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Suggested cleaned version:
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                "{validation.cleanedText}"
              </Typography>
            </Box>
          )}
        </Alert>
      )}

      {/* Character count for multiline fields */}
      {multiline && value && (
        <FormHelperText sx={{ textAlign: 'right', mt: 0.5 }}>
          {value.length} characters
        </FormHelperText>
      )}
    </Box>
  );
};

export default ProfanityAwareTextField;
